import { createTheme } from '@mui/material/styles';

export function createAppTheme(isDark) {
  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary:    { main: '#6366f1', light: '#818cf8', dark: '#4f46e5', contrastText: '#ffffff' },
      secondary:  { main: '#f97316', light: '#fb923c', dark: '#ea580c', contrastText: '#ffffff' },
      success:    { main: '#22c55e', light: '#4ade80', dark: '#16a34a' },
      error:      { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
      warning:    { main: '#f59e0b', light: '#fbbf24' },
      background: isDark
        ? { default: '#0a0f1e', paper: '#1e293b' }
        : { default: '#f0f4f8', paper: '#ffffff' },
      text: isDark
        ? { primary: '#f1f5f9', secondary: '#94a3b8', disabled: '#64748b' }
        : { primary: '#0f172a', secondary: '#334d66', disabled: '#7090ab' },
      divider: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.1)',
    },
    typography: {
      fontFamily: ['-apple-system', 'BlinkMacSystemFont', '"Inter"', '"Segoe UI"', 'Roboto', 'sans-serif'].join(','),
      h1: { fontWeight: 700, letterSpacing: '-0.03em' },
      h2: { fontWeight: 700, letterSpacing: '-0.025em' },
      h3: { fontWeight: 700, letterSpacing: '-0.02em' },
      h4: { fontWeight: 600, letterSpacing: '-0.015em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 500 },
      body1: { fontSize: '0.9375rem' },
      body2: { fontSize: '0.8125rem' },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
      overline: { letterSpacing: '0.08em', fontWeight: 600 },
    },
    shape: { borderRadius: 8 },
    shadows: [
      'none',
      isDark ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
      isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
      isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 24px rgba(0,0,0,0.1)',
      isDark ? '0 12px 32px rgba(0,0,0,0.4)' : '0 12px 32px rgba(0,0,0,0.12)',
      ...Array(20).fill(isDark ? '0 12px 32px rgba(0,0,0,0.4)' : '0 12px 32px rgba(0,0,0,0.12)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#0a0f1e' : '#f0f4f8',
            fontFeatureSettings: '"tnum"',
            scrollbarColor: isDark ? '#1e2d45 transparent' : '#b8cfe0 transparent',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 6, padding: '6px 16px', fontSize: '0.8125rem', boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          sizeLarge: { padding: '10px 22px', fontSize: '0.9rem' },
          sizeSmall: { padding: '4px 10px', fontSize: '0.75rem' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12, border: 'none', backgroundImage: 'none',
            boxShadow: isDark
              ? '0 0 0 1px rgba(255,255,255,0.05), 0 2px 8px rgba(0,0,0,0.35)'
              : '0 0 0 1px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 12, backgroundImage: 'none' },
          outlined: { border: isDark ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.1)' },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: isDark ? '#0d1424' : '#e4ecf4',
              color: isDark ? '#94a3b8' : '#334d66',
              fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.06em',
              borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.1)',
              padding: '10px 16px',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-body': {
              fontSize: '0.8125rem',
              color: isDark ? '#f1f5f9' : '#0f172a',
              padding: '12px 16px',
              borderBottom: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.06)',
            },
            '& .MuiTableRow-root:last-child .MuiTableCell-body': { borderBottom: 'none' },
            '& .MuiTableRow-root:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            },
          },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: 0 } } },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              fontSize: '0.875rem',
              '& fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.18)' },
              '&:hover fieldset': { borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.35)' },
              '&.Mui-focused fieldset': { borderColor: '#6366f1' },
            },
            '& .MuiInputLabel-root': { fontSize: '0.875rem' },
          },
        },
      },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiChip: { styleOverrides: { root: { borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, height: 22 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 8, fontSize: '0.8125rem', fontWeight: 500, padding: '8px 16px' } } },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.1)',
            boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(0,0,0,0.2)',
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle:   { styleOverrides: { root: { fontSize: '1rem', fontWeight: 700, padding: '20px 24px 12px' } } },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 24px 16px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px', gap: '8px' } } },
      MuiLinearProgress: { styleOverrides: { root: { borderRadius: 4, height: 6 } } },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0a0f1e' : '#ffffff',
            boxShadow: 'none',
            borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.1)',
          },
        },
      },
      MuiTab:     { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minHeight: 40 } } },
      MuiDivider: { styleOverrides: { root: { borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)' } } },
    },
  });
}

// Default dark theme for backward compat
export default createAppTheme(true);
